<script setup lang="ts">
import { toRefs, ref, watchEffect } from 'vue';
import Calendar from 'primevue/calendar';
import { useOrganizationContractsStore } from '@/store';
import { useDateTime } from '@/composables';
import { DisciplineInterface } from '@/types';
import { EMIT } from '@/enum';

interface PropsInterface {
  discipline: DisciplineInterface;
}

// the ConfirmAction emit already has the cancel emit attached to it,
// so cancel is getting called twice, causing errors in the parent.
const removeCancelEmit = `${EMIT.REMOVE}-${EMIT.CANCEL}`;
const emit = defineEmits<{
  (e: typeof removeCancelEmit): void;
  (e: EMIT.REMOVE, endDates: Record<string, string>): void;
}>();
const props = defineProps<PropsInterface>();
const { discipline } = toRefs(props);
const contractStore = useOrganizationContractsStore();
const endDate = ref<Date>();
const serviceEndDate = ref<Date>();
const eventsRemoved = ref<number>(0);
const serviceProvidersCount = ref<number>(0);

const onCancel = () => emit(removeCancelEmit);

const onRemove = () =>
  emit(EMIT.REMOVE, {
    schedule_by_date: useDateTime().formatDateForApi(endDate.value),
    document_by_date: useDateTime().formatDateForApi(serviceEndDate.value),
  });

watchEffect(async () => {
  if (endDate.value) {
    const { events_count, service_provider_count } = await contractStore.checkEventToRemove({
      discipline_id: discipline.value.discipline_id,
      start_at: useDateTime().formatDateForApi(contractStore.contract.start_at),
      end_at: useDateTime().formatDateForApi(endDate.value),
    });
    eventsRemoved.value = events_count;
    serviceProvidersCount.value = service_provider_count;
  }
});
</script>

<template>
  <ConfirmAction
    header="Remove Discipline"
    class="remove-discipline-modal"
    :style="{ width: '30rem' }"
    content-class="pb-0"
    :show-footer="true"
    accept-label="Remove Discipline"
    @accept="onRemove"
    @cancel="onCancel"
  >
    <div class="card">
      <div class="mb-2 field required">
        <label for="end_date">
          End Date
          <span class="sub-text">(no new events of this discipline after:)</span>
        </label>
        <Calendar id="end_date" v-model="endDate" class="w-full" placeholder="01/01/2001" date-format="mm/dd/yy" />
      </div>
      <div class="mb-2 field required">
        <label for="service_end_date">
          Service End Date
          <span class="sub-text">(no documenting events of this discipline after: )</span>
        </label>
        <Calendar
          id="service_end_date"
          v-model="serviceEndDate"
          class="w-full"
          placeholder="01/01/2001"
          date-format="mm/dd/yy"
        />
      </div>
    </div>
    <div v-if="endDate && serviceEndDate" class="mt-4 flex">
      <Icon name="warning" class="confirm-dialog-icon text-gray-600" />
      <div class="ml-3">
        <p class="remove-notice mb-2">
          Removing
          <b>{{ discipline.name }}</b>
          from this contract will remove
          <b>{{ eventsRemoved }}</b>
          events scheduled after
          <b>{{ Intl.DateTimeFormat().format(endDate) }}</b>
          and will prevent new events with this discipline from being created after this date.
        </p>
        <p class="remove-notice">
          <b>{{ serviceProvidersCount }}</b>
          Service Providers will have until
          <b>{{ Intl.DateTimeFormat().format(serviceEndDate) }}</b>
          to create or document any events of this discipline.
        </p>
      </div>
    </div>
  </ConfirmAction>
</template>

<style scoped lang="scss">
.remove-discipline-modal {
  label {
    color: $grayDark;
    font-size: 0.75rem;
    span.sub-text {
      color: $darkMedium;
    }
  }
  .remove-notice {
    color: $grayDark !important;
  }
}
</style>
